import { useCallback } from "react";
import { toast } from "sonner";
import { invalidateSearchCacheAction } from "@/lib/actions/search-cache-actions";
import { isSearchCacheResource } from "@/lib/actions/search-cache-types";
import type { BaseEntity, SimpleTableConfig, EntityHooks } from "../../UniversalCRMTable/types";
import type { GlobalDrawerStack, GlobalModalStack } from "./crudHandlerTypes";

/**
 * Handlers that actually perform the create/update/delete mutations once the
 * corresponding modal/drawer has captured user input.
 */
export function useCrudMutationHandlers<T extends BaseEntity>(
  entityName: string,
  config: SimpleTableConfig<T>,
  isMobile: boolean,
  createMutation: ReturnType<EntityHooks<T>["useCreate"]>,
  updateMutation: ReturnType<EntityHooks<T>["useUpdate"]>,
  deleteMutation: ReturnType<EntityHooks<T>["useDelete"]>,
  globalModalStack: GlobalModalStack,
  drawerStack: GlobalDrawerStack,
  editingItem: T | null,
  setEditingItem: (item: T | null) => void,
  deletingItem: T | null,
  setDeletingItem: (item: T | null) => void,
  refetch: () => void
) {
  const handleItemCreated = useCallback(
    async (newItem: Partial<T>) => {
      try {
        await createMutation.mutateAsync(newItem);
        // Close the appropriate modal/drawer based on device type
        if (isMobile) {
          drawerStack.closeDrawer(`create-${entityName}-drawer`);
        } else {
          globalModalStack.closeModal(`create-${entityName}`);
        }
        toast.success(`${config.displayName} created successfully`);
        // Invalidate search cache for this entity (server action)
        if (isSearchCacheResource(entityName)) {
          await invalidateSearchCacheAction(entityName);
        }
        refetch();
      } catch (error) {
        console.error(`Error creating ${entityName}:`, error);
        toast.error(
          `Failed to create ${entityName}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    },
    [
      createMutation,
      isMobile,
      drawerStack,
      globalModalStack,
      entityName,
      config.displayName,
      refetch,
    ]
  );

  const handleItemUpdated = useCallback(
    async (updatedItem: Partial<T>) => {
      try {
        if (!editingItem?.id) {
          throw new Error("No item selected for editing");
        }

        await updateMutation.mutateAsync({ ...updatedItem, id: editingItem.id });
        setEditingItem(null);
        globalModalStack.closeModal(`edit-${entityName}`);
        toast.success(`${config.displayName} updated successfully`);
        // Invalidate search cache for this entity (server action)
        if (isSearchCacheResource(entityName)) {
          await invalidateSearchCacheAction(entityName);
        }
        refetch();
      } catch (error) {
        console.error(`Error updating ${entityName}:`, error);
        toast.error(
          `Failed to update ${entityName}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    },
    [
      updateMutation,
      editingItem,
      setEditingItem,
      globalModalStack,
      entityName,
      config.displayName,
      refetch,
    ]
  );

  const handleItemDeleted = useCallback(async () => {
    if (deletingItem) {
      try {
        await deleteMutation.mutateAsync(deletingItem.id);
        toast.success(`${config.displayName} deleted successfully`);
        setDeletingItem(null);
        globalModalStack.closeModal(`delete-${entityName}`);
        // Invalidate search cache for this entity (server action)
        if (isSearchCacheResource(entityName)) {
          await invalidateSearchCacheAction(entityName);
        }
        refetch();
      } catch (error) {
        console.error("Delete error:", error);
        toast.error(`Failed to delete ${config.displayName}`);
      }
    }
  }, [
    deleteMutation,
    deletingItem,
    setDeletingItem,
    globalModalStack,
    entityName,
    config.displayName,
    refetch,
  ]);

  return {
    handleItemCreated,
    handleItemUpdated,
    handleItemDeleted,
  };
}
